import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Home, Upload, Clock } from 'lucide-react';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`, {
          headers: {
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }

        const data = await response.json();
        setPaymentDetails(data);

        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          // Store the purchased package_id for the upload page
          if (data.package_id) {
            localStorage.setItem('purchased_package', data.package_id);
            // Clear any old selected_package to prevent bypass
            localStorage.removeItem('selected_package');
          }
          // Automatically redirect to upload page after 2 seconds
          setTimeout(() => {
            navigate('/upload');
          }, 2000);
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus('expired');
          return;
        }

        // If payment is still pending and we haven't exceeded max attempts, continue polling
        setAttempts(prev => prev + 1);
        if (attempts < 5) {
          setTimeout(checkPaymentStatus, 2000); // Poll every 2 seconds
        } else {
          setPaymentStatus('timeout');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setPaymentStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId, navigate, attempts]);

  const getStatusDisplay = () => {
    switch (paymentStatus) {
      case 'checking':
        return {
          icon: <Clock className="w-16 h-16 text-blue-500 animate-pulse" />,
          title: 'Verifying Payment...',
          description: 'Please wait while we confirm your payment.',
          color: 'blue'
        };
      case 'success':
        return {
          icon: <CheckCircle className="w-16 h-16 text-green-500" />,
          title: 'Payment Successful!',
          description: 'Redirecting you to the upload page...',
          color: 'green'
        };
      case 'expired':
        return {
          icon: <Clock className="w-16 h-16 text-orange-500" />,
          title: 'Payment Session Expired',
          description: 'Your payment session has expired. Please try again.',
          color: 'orange'
        };
      case 'timeout':
        return {
          icon: <Clock className="w-16 h-16 text-yellow-500" />,
          title: 'Payment Verification Timeout',
          description: 'We\'re still processing your payment. Please check your email for confirmation or try again.',
          color: 'yellow'
        };
      default:
        return {
          icon: <Clock className="w-16 h-16 text-red-500" />,
          title: 'Payment Verification Error',
          description: 'There was an error verifying your payment. Please contact support if you were charged.',
          color: 'red'
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center page-transition">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="card text-center">
          <div className="mb-8">
            {statusDisplay.icon}
          </div>
          
          <h1 className="text-3xl font-bold mb-4 serif-display">
            {statusDisplay.title}
          </h1>
          
          <p className="text-xl text-gray-600 mb-8">
            {statusDisplay.description}
          </p>

          {/* Payment Details */}
          {paymentDetails && paymentStatus === 'success' && (
            <div className="bg-green-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-green-900 mb-4">Payment Details</h3>
              <div className="space-y-2 text-green-800">
                <div className="flex justify-between">
                  <span>Amount:</span>
                  <span className="font-semibold">
                    ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className="font-semibold capitalize">{paymentDetails.payment_status}</span>
                </div>
                <div className="flex justify-between">
                  <span>Session ID:</span>
                  <span className="font-mono text-sm">{sessionId.substring(0, 20)}...</span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-4">
            {paymentStatus === 'success' && (
              <>
                <button
                  onClick={() => navigate('/upload')}
                  className="btn-primary flex items-center space-x-2 mx-auto"
                >
                  <Upload className="w-5 h-5" />
                  <span>Start Your Analysis</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                
                <div className="text-sm text-gray-500">
                  <p>Your payment has been confirmed. You can now upload content for analysis.</p>
                </div>
              </>
            )}

            {(paymentStatus === 'expired' || paymentStatus === 'error') && (
              <>
                <button
                  onClick={() => navigate('/pricing')}
                  className="btn-primary"
                >
                  Try Again
                </button>
                
                <div className="text-sm text-gray-500">
                  <p>If you were charged but see this error, please contact our support team.</p>
                </div>
              </>
            )}

            {paymentStatus === 'timeout' && (
              <>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => window.location.reload()}
                    className="btn-primary"
                  >
                    Check Again
                  </button>
                  <button
                    onClick={() => navigate('/upload')}
                    className="btn-secondary"
                  >
                    Continue Anyway
                  </button>
                </div>
                
                <div className="text-sm text-gray-500">
                  <p>Payment processing can sometimes take a few minutes. You should receive an email confirmation shortly.</p>
                </div>
              </>
            )}
          </div>

          {/* Success Features */}
          {paymentStatus === 'success' && (
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold mb-4">What's Next?</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-purple-600 font-bold">1</span>
                  </div>
                  <p className="font-medium">Upload Content</p>
                  <p className="text-gray-600">Upload PDFs, images, or paste text</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-purple-600 font-bold">2</span>
                  </div>
                  <p className="font-medium">AI Analysis</p>
                  <p className="text-gray-600">Advanced AI analyzes patterns</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-purple-600 font-bold">3</span>
                  </div>
                  <p className="font-medium">Get Report</p>
                  <p className="text-gray-600">Download professional PDF</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            🔒 Your payment information is processed securely through Stripe. 
            We do not store your payment details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;